import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Stack, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { EDIT_KEYBOARD_HELP } from './WidgetGrid.jsx';

/**
 * Customize · Done · Cancel for a dashboard, plus the "Removed <widget>" undo snackbar
 * (Beta-1 plan P3-16). Drives a `useDashboardEditor` instance; drop it next to the grid.
 *
 *   <EditModeControls editor={editor} />
 *
 * Props
 *   editor          value of useDashboardEditor()
 *   labels          { customize, done, cancel, removed(title) } overrides
 *   onAdd           when given, an "Add widget" (+) button appears in edit mode — opens the gallery (P3-17)
 *   onReset         when given, a "Reset to default" button appears in edit mode (P3-19); after the
 *                   user confirms in a dialog, the caller restores the role default
 *                   (useDashboardLayout.reset + editor.replace)
 *   extra           node rendered after the buttons
 *   size            MUI button size (default 'small')
 *   escapeCancels   Esc leaves edit mode and discards (default true)
 */
export function EditModeControls({ editor, labels = {}, onAdd, onReset, extra = null, size = 'small', escapeCancels = true, sx }) {
  const { editing, dirty, removed, begin, done, cancel, undoRemove, dismissUndo } = editor;
  const [confirmReset, setConfirmReset] = useState(false);
  const text = {
    customize: 'Customize',
    done: 'Done',
    cancel: 'Cancel',
    add: 'Add widget',
    reset: 'Reset to default',
    resetTitle: 'Reset this dashboard?',
    resetBody: 'The default layout for your access level will replace your customizations. Widgets you added and sizes you chose here will be lost.',
    removed: (title) => `Removed ${title}`,
    ...labels,
  };

  useEffect(() => {
    if (!editing || !escapeCancels) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const tag = event.target && event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      cancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, escapeCancels, cancel]);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={sx}>
        {editing ? (
          <>
            <Tooltip title={EDIT_KEYBOARD_HELP}>
              <Button variant="contained" size={size} startIcon={<DoneIcon />} onClick={done} aria-label={text.done}>
                {text.done}
              </Button>
            </Tooltip>
            <Button variant="text" size={size} startIcon={<CloseIcon />} onClick={cancel} aria-label={dirty ? `${text.cancel} (discard changes)` : text.cancel}>
              {text.cancel}
            </Button>
            {onAdd && (
              <Button variant="outlined" size={size} startIcon={<AddIcon />} onClick={onAdd} aria-label={text.add}>
                {text.add}
              </Button>
            )}
            {onReset && (
              <Button variant="text" size={size} color="inherit" startIcon={<RestartAltIcon />} onClick={() => setConfirmReset(true)} aria-label={text.reset}>
                {text.reset}
              </Button>
            )}
          </>
        ) : (
          <Button variant="outlined" size={size} startIcon={<TuneIcon />} onClick={begin} aria-label={text.customize}>
            {text.customize}
          </Button>
        )}
        {extra}
      </Stack>

      {onReset && (
        <Dialog open={confirmReset} onClose={() => setConfirmReset(false)} aria-labelledby="reset-dashboard-title">
          <DialogTitle id="reset-dashboard-title">{text.resetTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{text.resetBody}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmReset(false)}>Keep my layout</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setConfirmReset(false);
                onReset();
              }}
            >
              {text.reset}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={Boolean(removed)}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== 'clickaway' && dismissUndo()}
        message={removed ? text.removed(removed.title) : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Button color="inherit" size="small" onClick={undoRemove} sx={{ fontWeight: 700 }}>
            Undo
          </Button>
        }
      />
    </>
  );
}

export default EditModeControls;
